import { useState, type ComponentType } from "react";
import { EVENT_TEMPLATE_DEFAULT, type EventTemplateConfig, type EventTemplateId } from "@/lib/event-templates";
import type { PublicEvent } from "@/services/api/publicEvents";
import { BotezBokehTemplate } from "./BotezBokehTemplate";
import { GoldenSerenityTemplate } from "./GoldenSerenityTemplate";
import { PublicRsvpForm } from "@/components/public/PublicRsvpForm";

type Translator = (key: string, params?: Record<string, string | number | undefined>) => string;

type TemplateRendererProps = {
  event: PublicEvent;
  config: EventTemplateConfig;
  locale: string;
  t: Translator;
  onShare: () => Promise<void> | void;
  onRsvpClick?: () => void;
};

/** Every design the app can render, keyed by theme. Kept in sync with `SELECTABLE_TEMPLATE_KEYS`. */
const TEMPLATE_COMPONENTS: Record<EventTemplateId, ComponentType<TemplateRendererProps>> = {
  botez_bokeh: BotezBokehTemplate,
  golden_serenity: GoldenSerenityTemplate,
};

// Helper function to check if RSVP cutoff date has passed
function isRsvpCutoffPassed(event: { rsvpCutoffDate?: string | null }): boolean {
  if (!event.rsvpCutoffDate) {
    return false; // No cutoff set, always allow RSVP
  }
  const now = new Date();
  const cutoffDate = new Date(event.rsvpCutoffDate);
  // Compare dates only, ignore time for cutoff
  now.setHours(0, 0, 0, 0);
  cutoffDate.setHours(0, 0, 0, 0);
  return now > cutoffDate;
}

export function EventTemplateRenderer(props: TemplateRendererProps): JSX.Element {
  // Get template key from event theme or template definition
  const templateKey = props.event.theme || props.event.template?.key;

  // RSVP dialog state management
  const [rsvpDialogOpen, setRsvpDialogOpen] = useState(false);
  const rsvpProps = {
    ...props,
    onRsvpClick: () => setRsvpDialogOpen(true),
  };

  // Any unknown or retired key (an event row that escaped the JUB-112
  // migration, a stale cache, ...) renders EVENT_TEMPLATE_DEFAULT rather
  // than throwing or rendering blank.
  const Template =
    TEMPLATE_COMPONENTS[templateKey as EventTemplateId] ?? TEMPLATE_COMPONENTS[EVENT_TEMPLATE_DEFAULT];
  const templateContent = <Template {...rsvpProps} />;

  return (
    <>
      {templateContent}
      {/* RSVP Form Dialog for all templates */}
      {props.event.rsvpSettings?.rsvpEnabled && !isRsvpCutoffPassed(props.event) && (
        <PublicRsvpForm
          event={props.event}
          config={props.config}
          locale={props.locale}
          open={rsvpDialogOpen}
          onOpenChange={setRsvpDialogOpen}
        />
      )}
    </>
  );
}

// Keep the function export for backward compatibility
export function renderEventTemplate(props: TemplateRendererProps): JSX.Element {
  return <EventTemplateRenderer {...props} />;
}
